/**
 * 唯一 HTTP 客户端：所有模块经由它访问 /api/v1/web，不各写 fetch。
 * - 默认同源 cookie（credentials: "include"），写操作自动回填 X-CSRF-Token；
 * - 显式访客用 publicGuestGet 读白名单详情、publicGuestWorldGet 读固定星球入口；首次即 omit，不在 401 后降级；
 * - 需要幂等的写操作传入 idempotencyKey（同一次用户动作重试复用同一个键）；
 * - 统一把错误信封/网络失败/超时归一为 ApiError，并保留 request_id（只给日志和运营后台查，不上玩家页面：X-09）；
 *   没有错误信封的回包、超时按状态说人话（errors.ts 的 PLAYER_STATUS_TEXT，ERROR-RECOVERY-01 / Q-204d）。
 */

import type { WebErrorEnvelope } from "@/shared/contracts";
import { env } from "@/shared/config/env";
import { ApiError, PLAYER_STATUS_TEXT, playerTextForStatus } from "./errors";

const CSRF_COOKIE = "petsoul_csrf";
const DEFAULT_TIMEOUT_MS = 15_000;
const MAX_TIMEOUT_MS = 2_147_483_647;

export interface RequestOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  query?: Record<string, string | number | boolean | undefined | null>;
  body?: unknown;
  idempotencyKey?: string;
  signal?: AbortSignal;
  timeoutMs?: number;
}

/** 只读公开详情；不接收自定义 headers、body、query 或凭据策略。 */
export interface PublicGuestGetOptions {
  method?: "GET";
  signal?: AbortSignal;
  timeoutMs?: number;
}

function validateGuestRead(base: string, path: string, options: PublicGuestGetOptions): PublicGuestGetOptions {
  const reject = () => new ApiError({ kind: "http", status: 400, code: "VALIDATION_FAILED", message: "不能以访客方式打开这里。", details: { reason: "guest_read_not_allowed" } });
  const prefix = "/public/pets/";
  if (typeof path !== "string" || !path.startsWith(prefix)) throw reject();
  const petId = path.slice(prefix.length);
  // Canonical IDs only: reject encoded/double-encoded separators, dot segments,
  // query/fragment, backslashes and whitespace before fetch/URL normalization.
  if (!petId || /[^A-Za-z0-9_-]/.test(petId)) throw reject();
  return validateGuestOptions(base, options);
}

/** Shared validation only; each capability owns its exact path separately. */
function validateGuestOptions(base: string, options: PublicGuestGetOptions): PublicGuestGetOptions {
  const reject = () => new ApiError({ kind: "http", status: 400, code: "VALIDATION_FAILED", message: "不能以访客方式打开这里。", details: { reason: "guest_read_not_allowed" } });
  // Only own data properties of a plain/null-prototype options bag are allowed.
  // Do not invoke accessors or re-read caller properties after validating them.
  const safeOptions: PublicGuestGetOptions = Object.create(null);
  try {
    if (!options || typeof options !== "object" || Array.isArray(options)) throw reject();
    const prototype = Object.getPrototypeOf(options);
    if (prototype !== null && prototype !== Object.prototype) throw reject();
    for (const key of Reflect.ownKeys(options)) {
      if (typeof key !== "string" || !["method", "signal", "timeoutMs"].includes(key)) throw reject();
      const descriptor = Object.getOwnPropertyDescriptor(options, key);
      if (!descriptor || !Object.hasOwn(descriptor, "value")) throw reject();
      Object.defineProperty(safeOptions, key, { value: descriptor.value });
    }
    if (safeOptions.method !== undefined && safeOptions.method !== "GET") throw reject();
    const timeoutMs = safeOptions.timeoutMs;
    if (timeoutMs !== undefined && (typeof timeoutMs !== "number" || !Number.isFinite(timeoutMs)
        || timeoutMs < 0 || timeoutMs > MAX_TIMEOUT_MS)) throw reject();
    const signal = safeOptions.signal;
    if (signal !== undefined) {
      if (signal === null || typeof signal !== "object") throw reject();
      // Native signals may own implementation symbols, but caller accessors and
      // shadowed methods must never run. A prototype/duck-type check alone is
      // insufficient: the intrinsic getter verifies the native signal brand.
      for (const key of Reflect.ownKeys(signal)) {
        const descriptor = Object.getOwnPropertyDescriptor(signal, key);
        if (typeof key === "string" || !descriptor || !Object.hasOwn(descriptor, "value")) throw reject();
      }
      const aborted = Object.getOwnPropertyDescriptor(AbortSignal.prototype, "aborted")?.get;
      if (!aborted || typeof aborted.call(signal) !== "boolean") throw reject();
    }
  } catch { throw reject(); }
  let target: URL;
  try { target = new URL(base, "https://guest-read.invalid"); } catch { throw reject(); }
  // Configured absolute API origins are supported, but a base must not smuggle
  // credentials, a query, redirects or a different/normalized private path.
  if (!["http:", "https:"].includes(target.protocol) || target.pathname !== "/api/v1/web"
      || target.username || target.password || target.search || target.hash
      || (base !== "/api/v1/web" && base !== `${target.origin}/api/v1/web`)) throw reject();
  return safeOptions;
}

function readCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.split("; ").find((part) => part.startsWith(`${name}=`));
  return match ? decodeURIComponent(match.slice(name.length + 1)) : null;
}

function buildUrl(base: string, path: string, query?: RequestOptions["query"]): string {
  const url = `${base}${path.startsWith("/") ? path : `/${path}`}`;
  if (!query) return url;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== null) params.set(key, String(value));
  }
  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
}

function isEnvelope(value: unknown): value is WebErrorEnvelope {
  return typeof value === "object" && value !== null && "error" in value && typeof (value as WebErrorEnvelope).error?.code === "string";
}


/** A narrow binary port for a currently bound public official identity image. */
export interface PublicOfficialIdentityMedia {
  petId: string;
  revision: number;
  url: string;
  /** 缩略档（P 的 PET-PHOTO-SIZES-01；/meta 有 pets.photo_sizes 才带）：只收 96、192、384，请求地址后接 ?w=；url 仍是不带参数的规范地址（UI-REVIEW110-01） */
  width?: 96 | 192 | 384;
}

function validateOfficialIdentityMedia(base: string, input: PublicOfficialIdentityMedia): string {
  const reject = () => new ApiError({ kind: "http", status: 400, code: "VALIDATION_FAILED", message: "这张公开形象暂时打不开。", details: { reason: "guest_identity_media_not_allowed" } });
  const values: Record<string, unknown> = Object.create(null);
  try {
    if (!input || typeof input !== "object" || Array.isArray(input)
        || ![null, Object.prototype].includes(Object.getPrototypeOf(input))) throw reject();
    for (const key of Reflect.ownKeys(input)) {
      if (typeof key !== "string" || !["petId", "revision", "url", "width"].includes(key)) throw reject();
      const d = Object.getOwnPropertyDescriptor(input, key);
      if (!d || !Object.hasOwn(d, "value")) throw reject();
      values[key] = d.value;
    }
    if (typeof values.petId !== "string" || !/^[A-Za-z0-9_-]+$/.test(values.petId)
        || typeof values.revision !== "number" || !Number.isSafeInteger(values.revision) || values.revision <= 0) throw reject();
    const path = `/public/identity/${values.petId}/official/${values.revision}`;
    if (values.url !== base + path) throw reject();
    // 可选的缩略档只认闭集里的数，带了就接在规范地址后面（UI-REVIEW110-01）
    if ("width" in values && ![96, 192, 384].includes(values.width as number)) throw reject();
    return "width" in values ? `${path}?w=${values.width}` : path;
  } catch { throw reject(); }
}


export interface ApiClient {
  request<T>(path: string, options?: RequestOptions): Promise<T>;
  /** Optional for existing adapters; absence must fail closed, never fall back to request(). */
  publicGuestGet?<T>(path: string, options?: PublicGuestGetOptions): Promise<T>;
  /** Fixed /public/world GET only; absence fails closed. No arbitrary path or pagination. */
  publicGuestWorldGet?<T>(options?: PublicGuestGetOptions): Promise<T>;
  /** Public Studio list/detail only; absence fails closed without private fallback. */
  publicGuestStudioGet?<T>(path: string, options?: PublicGuestGetOptions): Promise<T>;
  /** Strict public official image only; no arbitrary anonymous binary URLs. */
  publicGuestOfficialIdentityGet?(input: PublicOfficialIdentityMedia, options?: PublicGuestGetOptions): Promise<Blob>;
  /** 公开居民时间线（UI-WORLD-LIVING-117-3）：只认规范编号，自己拼 /public/residents/{id}/timeline，不带分页参数；缺了就关着，不退回带凭据的读法。 */
  publicGuestResidentTimelineGet?<T>(petId: string, options?: PublicGuestGetOptions): Promise<T>;
  readonly base: string;
}

export interface GuestApiClient extends ApiClient {
  publicGuestOfficialIdentityGet(input: PublicOfficialIdentityMedia, options?: PublicGuestGetOptions): Promise<Blob>;
  publicGuestGet<T>(path: string, options?: PublicGuestGetOptions): Promise<T>;
  publicGuestWorldGet<T>(options?: PublicGuestGetOptions): Promise<T>;
  publicGuestStudioGet<T>(path: string, options?: PublicGuestGetOptions): Promise<T>;
  publicGuestResidentTimelineGet<T>(petId: string, options?: PublicGuestGetOptions): Promise<T>;
}

export function createApiClient(base: string = env.apiBase, fetchImpl: typeof fetch = (...args) => fetch(...args)): GuestApiClient {
  async function send<T>(path: string, options: RequestOptions, guest = false): Promise<T> {
    const method = options.method ?? "GET";
    const headers: Record<string, string> = { Accept: "application/json" };
    const isForm = typeof FormData !== "undefined" && options.body instanceof FormData;
    // FormData（照片上传）由浏览器生成 multipart 边界，不手动设置 Content-Type。
    if (options.body !== undefined && !isForm) headers["Content-Type"] = "application/json";
    if (method !== "GET") {
      const csrf = readCookie(CSRF_COOKIE);
      if (csrf) headers["X-CSRF-Token"] = csrf;
    }
    if (options.idempotencyKey) headers["Idempotency-Key"] = options.idempotencyKey;

    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort("timeout"), options.timeoutMs ?? DEFAULT_TIMEOUT_MS);
    const guestSignal = guest ? options.signal : undefined;
    const abortGuest = () => controller.abort("aborted");
    if (guestSignal) {
      // Use native operations, not caller-overridable signal properties.
      const aborted = Object.getOwnPropertyDescriptor(AbortSignal.prototype, "aborted")!.get!;
      if (aborted.call(guestSignal)) abortGuest();
      else AbortSignal.prototype.addEventListener.call(guestSignal, "abort", abortGuest, { once: true });
    } else if (!guest) {
      options.signal?.addEventListener("abort", () => controller.abort("aborted"), { once: true });
    }

    let response: Response;
    try {
      response = await fetchImpl(buildUrl(base, path, options.query), {
        method,
        headers,
        body: options.body === undefined ? undefined : isForm ? (options.body as FormData) : JSON.stringify(options.body),
        credentials: guest ? "omit" : "include",
        ...(guest ? { redirect: "error" as const } : {}),
        signal: controller.signal,
      });
    } catch (cause) {
      const reason = controller.signal.reason;
      if (reason === "timeout") {
        throw new ApiError({ kind: "timeout", code: "TIMEOUT", message: PLAYER_STATUS_TEXT.timeout, retryable: true });
      }
      if (reason === "aborted") {
        throw new ApiError({ kind: "aborted", code: "ABORTED", message: "请求已取消。" });
      }
      throw new ApiError({
        kind: "network",
        code: "NETWORK_ERROR",
        message: "暂时连不上，正在重新连接，稍等一下再看看。",
        retryable: true,
        details: { cause: cause instanceof Error ? cause.message : String(cause) },
      });
    } finally {
      clearTimeout(timeout);
    }

    const requestId = response.headers.get("X-Request-ID");
    if (response.status === 204) return undefined as T;
    const text = await response.text();
    let payload: unknown = null;
    if (text) {
      try {
        payload = JSON.parse(text);
      } catch {
        payload = null;
      }
    }
    if (!response.ok) {
      if (isEnvelope(payload)) throw ApiError.fromEnvelope(response.status, payload, requestId);
      throw new ApiError({
        kind: "http",
        status: response.status,
        code: response.status >= 500 ? "UPSTREAM_UNAVAILABLE" : "INTERNAL_ERROR",
        message: playerTextForStatus(response.status),
        requestId,
        retryable: response.status >= 500,
      });
    }
    return payload as T;
  }

  function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
    return send<T>(path, options);
  }

  async function publicGuestGet<T>(path: string, options: PublicGuestGetOptions = {}): Promise<T> {
    const safeOptions = validateGuestRead(base, path, options);
    return send<T>(path, safeOptions, true);
  }

  async function publicGuestWorldGet<T>(options: PublicGuestGetOptions = {}): Promise<T> {
    const safeOptions = validateGuestOptions(base, options);
    return send<T>("/public/world", safeOptions, true);
  }

  async function publicGuestStudioGet<T>(path: string, options: PublicGuestGetOptions = {}): Promise<T> {
    const prefix = "/studio/calls/";
    if (typeof path !== "string" || (path !== "/studio/calls" &&
        (!path.startsWith(prefix) || !path.slice(prefix.length) || path.slice(prefix.length).length > 128 ||
          /[^A-Za-z0-9_-]/.test(path.slice(prefix.length))))) {
      throw new ApiError({ kind: "http", status: 400, code: "VALIDATION_FAILED", message: "不能以访客方式打开这里。", details: { reason: "guest_read_not_allowed" } });
    }
    const safeOptions = validateGuestOptions(base, options);
    return send<T>(path, safeOptions, true);
  }

  async function publicGuestResidentTimelineGet<T>(petId: string, options: PublicGuestGetOptions = {}): Promise<T> {
    // 和详情同一套规范编号（不收编码过的分隔符、点段、查询串、空白），路径自己拼，不接收调用方给的路径
    if (typeof petId !== "string" || !petId || petId.length > 128 || /[^A-Za-z0-9_-]/.test(petId)) {
      throw new ApiError({ kind: "http", status: 400, code: "VALIDATION_FAILED", message: "不能以访客方式打开这里。", details: { reason: "guest_read_not_allowed" } });
    }
    const safeOptions = validateGuestOptions(base, options);
    return send<T>(`/public/residents/${petId}/timeline`, safeOptions, true);
  }


  async function publicGuestOfficialIdentityGet(input: PublicOfficialIdentityMedia, options: PublicGuestGetOptions = {}): Promise<Blob> {
    const path = validateOfficialIdentityMedia(base, input);
    const safe = validateGuestOptions(base, options);
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort("timeout"), safe.timeoutMs ?? DEFAULT_TIMEOUT_MS);
    const external = safe.signal;
    const abort = () => controller.abort("aborted");
    if (external) {
      const getter = Object.getOwnPropertyDescriptor(AbortSignal.prototype, "aborted")!.get!;
      if (getter.call(external)) abort();
      else AbortSignal.prototype.addEventListener.call(external, "abort", abort, { once: true });
    }
    try {
      if (controller.signal.aborted) throw new Error("aborted");
      const response = await fetchImpl(base + path, {
        method: "GET", credentials: "omit", redirect: "error", cache: "no-store",
        headers: { Accept: "image/png,image/jpeg,image/webp,image/gif" }, signal: controller.signal,
      });
      if (controller.signal.aborted) throw new Error("aborted");
      const expected = new URL(base + path, typeof location === "undefined" ? "https://guest-read.invalid" : location.origin).href;
      if (response.redirected || response.type === "opaqueredirect" || (response.url && response.url !== expected)) {
        throw new ApiError({ kind: "http", status: 400, code: "VALIDATION_FAILED", message: "这张公开形象暂时打不开。" });
      }
      if (!response.ok) {
        let payload: unknown = null;
        try { payload = JSON.parse(await response.text()); } catch { /* Preserve original HTTP status without fallback. */ }
        if (isEnvelope(payload)) throw ApiError.fromEnvelope(response.status, payload, response.headers.get("X-Request-ID"));
        throw new ApiError({ kind: "http", status: response.status, code: "INTERNAL_ERROR", message: playerTextForStatus(response.status), retryable: false });
      }
      const mime = response.headers.get("Content-Type")?.split(";")[0].trim().toLowerCase();
      if (!mime || !["image/png", "image/jpeg", "image/webp", "image/gif"].includes(mime)) {
        throw new ApiError({ kind: "http", status: 400, code: "VALIDATION_FAILED", message: "这张公开形象暂时打不开。" });
      }
      const blob = await response.blob();
      if (controller.signal.aborted) throw new Error("aborted");
      if (!blob.size || blob.type.split(";")[0].trim().toLowerCase() !== mime) {
        throw new ApiError({ kind: "http", status: 400, code: "VALIDATION_FAILED", message: "这张公开形象暂时打不开。" });
      }
      return blob;
    } catch (cause) {
      if (controller.signal.aborted) {
        throw new ApiError({ kind: controller.signal.reason === "timeout" ? "timeout" : "aborted", code: controller.signal.reason === "timeout" ? "TIMEOUT" : "ABORTED", message: controller.signal.reason === "timeout" ? PLAYER_STATUS_TEXT.timeout : "请求已取消。" });
      }
      if (cause instanceof ApiError) throw cause;
      throw new ApiError({ kind: "network", code: "NETWORK_ERROR", message: "这张公开形象暂时打不开。", retryable: false });
    } finally {
      clearTimeout(timeout);
      if (external) AbortSignal.prototype.removeEventListener.call(external, "abort", abort);
    }
  }


  return { request, publicGuestGet, publicGuestWorldGet, publicGuestStudioGet, publicGuestOfficialIdentityGet, publicGuestResidentTimelineGet, base };
}

export const apiClient = createApiClient();
